import { createRoot } from 'react-dom/client';
import { mergeRight, prop } from 'ramda';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import {
  resourceReducer,
  getLocale,
  loadTexts,
  loadFont,
} from '@twnel/web-components';
import { getApiHost } from '@twnel/utils-js/web';
import { reducer as sessionReducer, LoginEnhancer } from '@twnel/companies-login';
import dataReducer from './data/reducer';
import visualBuilderReducer from './modules/VisualBuilder/connect/reducer';
import { GlobalStyle } from './modules/General';
import App from './modules/App';

// Toastify styles
import 'react-toastify/dist/ReactToastify.css';
// Datepicker styles
import 'react-datepicker/dist/react-datepicker.css';
// Common twnel css.
import '@twnel/web-components/bundle.css';

const { VITE_APP_VERSION, VITE_TWNEL_ENV } = import.meta.env;
// @twnel/companies-login 2.x: LoginEnhancer ya no devuelve un store enhancer
// (función); devuelve { middleware, preloadedState }. El array de middleware
// incluye el ThunkMiddleware con getContext + SessionMiddleware que antes
// aportaba el enhancer de la API 1.x.
const { middleware: loginMiddleware, preloadedState: loginPreloadedState } = LoginEnhancer({
  environment: getApiHost() || VITE_TWNEL_ENV,
  debug: import.meta.env.DEV,
  version: VITE_APP_VERSION,
  options: {
    includeBusinessUnits: true,
  },
});

// Compose your reducer
const reducer = combineReducers({
  ...resourceReducer, // Manage async resources e.g. localized text files
  ...sessionReducer, // Manage session data
  ...dataReducer,
  ...visualBuilderReducer,
});

// Create your redux store.
// El middleware de @twnel/companies-login aporta el ThunkMiddleware con getContext
// + SessionMiddleware, por eso se desactiva el thunk por defecto de RTK. El
// preloadedState siembra el estado de sesión que antes inyectaba el enhancer.
const store = configureStore({
  reducer,
  preloadedState: loginPreloadedState,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    thunk: false,
    serializableCheck: false,
    immutableCheck: false,
  }).concat(loginMiddleware),
  devTools: import.meta.env.DEV,
});

// Load the texts file.
store.dispatch(
  loadTexts(async () => {
    const locale = getLocale();
    let loaders;
    switch (locale) {
      case 'es':
        loaders = [
          import('@twnel/web-components/lib/es.json'),
          import('./localization/es.json'),
        ];
        break;
      case 'pt':
        loaders = [
          import('@twnel/web-components/lib/pt.json'),
          import('./localization/pt.json'),
        ];
        break;
      default:
        return {};
    }
    const result = await Promise.all(loaders);
    return result.map(prop('default')).reduce(mergeRight);
  }),
);

store.dispatch(
  loadFont(() => import('@twnel/web-components/lib/quicksand-regular.json')),
);

// styled-components v6 dejó de filtrar props desconocidas hacia el DOM (la v5 sí
// lo hacía). Reactivamos ese filtrado globalmente: en elementos host (string)
// solo se reenvían atributos HTML válidos; a componentes React se les pasa todo.
// Como styled-components está deduplicado a una sola instancia, esto cubre también
// los styled de @twnel/web-components (props como actionable/boxed/tooltip).
const shouldForwardProp = (propName, target) => (
  typeof target === 'string' ? isPropValid(propName) : true
);

const root = createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <GlobalStyle />
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StyleSheetManager>
  </Provider>,
);
